import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpEvent, HttpParams, HttpResponse } from '@angular/common/http';
import { FileService } from '../file/file.service';
import { RockFile } from '../../core/models/rock-files.model';
import { UploadRockImageData } from '../../core/models/upload-rock-image.model';
import { UpscalingRockFile } from '../../core/models/upscaling-rock-files.model';

@Injectable({
  providedIn: 'root',
})
export class RockImagesService {
  private readonly apiUrl: string = '/simulation-api';
  private readonly rockFilesUrl: string = `${this.apiUrl}/SegmentedRockFile`;
  private readonly responseTypeTextOptions: any = {
    responseType: 'text',
  };

  constructor(private readonly httpClient: HttpClient, private readonly fileService: FileService) {}

  getRockFilesData(options: any, fileType?: string): Observable<RockFile[] | UpscalingRockFile[]> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.get<RockFile[] | UpscalingRockFile[]>(this.rockFilesUrl, {
      headers: options.headers,
      params
    });
  }

  uploadNewRockFile(uploadData: UploadRockImageData, options: any = {}, fileType?: string): Observable<HttpEvent<string>> {
    const formData = new FormData();

    if (!uploadData.file) {
      throw new Error('File is required');
    }

    formData.append('file', uploadData.file);

    let params = new HttpParams().set('fileFormat', uploadData.fileFormat);

    if (fileType) {
      params = params.set('fileType', fileType);
    }

    if (uploadData.fileFormat === 'raw') {
      if (uploadData.dataType) params = params.set('dataType', uploadData.dataType);
      if (uploadData.resolution) params = params.set('resolution', uploadData.resolution.toString());
      if (uploadData.voxelsX) params = params.set('voxelsX', uploadData.voxelsX.toString());
      if (uploadData.voxelsY) params = params.set('voxelsY', uploadData.voxelsY.toString());
      if (uploadData.voxelsZ) params = params.set('voxelsZ', uploadData.voxelsZ.toString());
    }

    return this.httpClient.post<string>(this.rockFilesUrl, formData, {
      ...this.responseTypeTextOptions,
      params,
      headers: options.headers,
    });
  }

  getRockFileMetadata(rockFileName: string, fileType?: string): Observable<RockFile | UpscalingRockFile> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.get<RockFile | UpscalingRockFile>(`${this.rockFilesUrl}/${rockFileName}/metadata`, { params });
  }

  downloadRockFileImage(rockFileName: string, fileType?: string): void {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    this.httpClient
      .get<Blob>(`${this.rockFilesUrl}/${rockFileName}`, {
        responseType: 'blob' as 'json',
        observe: 'response',
        params,
      })
      .subscribe((res: HttpResponse<Blob>) => {
        this.fileService.downloadFileFromBlobObject(
          res.body!,
          this.fileService.extractFileNameFromContentDispositionHeader(res),
        );
      });
  }

  deleteRockFileImage(rockFileName: string, fileType?: string): Observable<HttpEvent<string>> {
    let params = new HttpParams();
    if (fileType) {
      params = params.set('fileType', fileType);
    }
    return this.httpClient.delete<string>(
      `${this.rockFilesUrl}/${rockFileName}`,
      {
        ...this.responseTypeTextOptions,
        params,
      },
    );
  }
}
